import { Table, Tooltip, Tag, Space, Button } from "antd";
import moment from "moment";
import { Link } from "react-router-dom";

export default function VerificationsIndex(props) {
  const { verifications, loading, setSelectedVerification, userRole } = props;

  // 🔧 Helpers
  const getStatusInfo = (status) => {
    const map = {
      1: { color: "default", text: "En proceso" },
      2: { color: "DeepSkyBlue", text: "En revisión" },
      3: { color: "#52c41a", text: "Emitida" },
      4: { color: "#52c41a", text: "Pagada" },
      5: { color: "red", text: "Cancelada" },
    };
    return map[status] || { color: "default", text: "Desconocido" };
  };

  const renderStatus = (text, info, link) => {
    const content = (
      <Tag color={info.color} style={{ fontSize: 12, marginTop: 4 }}>
        {info.text}
      </Tag>
    );
    return link ? (
      <Link to={link}>
        <Tooltip title={info.text}>{content}</Tooltip>
      </Link>
    ) : (
      <Tooltip title={info.text}>{content}</Tooltip>
    );
  };

  const renderNullable = (value, formatter = (v) => v) => (value === null ? <span className="subtle-text">N/D</span> : formatter(value));

  // Get row class name based on status
  const getRowClassName = (record) => {
    const statusInfo = getStatusInfo(record.Status);
    let borderColor = statusInfo.color;

    // Handle special color mappings
    if (borderColor === "default") borderColor = "#d9d9d9";
    if (borderColor === "DeepSkyBlue") borderColor = "#00bfff";

    return `status-border-${record.Status}`;
  };

  // 📊 Column definitions
  const columns = [
    {
      title: "Folio",
      dataIndex: "ControlInterno",
      key: "ControlInterno",
      width: 140,
      fixed: "left",
      render: (text, record) => {
        if (text === null || text === undefined || text === "") {
          return <Link to={`/verificaciones/${record.VerificacionID}`}>¡Sin folio!</Link>;
        }
        if (userRole === "administrador") return text;
        if (record.VerificacionID) return <Link to={`/verificaciones/${record.VerificacionID}`}>{text}</Link>;
        return <span>Desconocido</span>;
      },
    },
    {
      title: "Estado",
      dataIndex: "Status",
      key: "Status",
      width: 120,
      render: (text, record) => {
        const info = getStatusInfo(text);
        return renderStatus(info.text, info);
      },
    },
    ...(userRole !== "verificador"
      ? [
          {
            title: "Verificador",
            dataIndex: "Verificador",
            key: "Verificador",
          },
        ]
      : []),
    {
      title: "Fecha",
      dataIndex: "FechaCreacion",
      key: "FechaCreacion",
      width: 120,
      responsive: ["md"],
      render: (text) =>
        renderNullable(text, (t) => (
          <>
            <div>{moment(t, "YYYY-MM-DD").format("DD/MM/YYYY")}</div>
            <div style={{ fontSize: "0.8em", color: "#999" }}>{moment(t, "YYYY-MM-DD").fromNow()}</div>
          </>
        )),
    },
    {
      title: "Descripcion",
      dataIndex: "DescripcionBreve",
      key: "DescripcionBreve",
      width: 200,
      responsive: ["lg"],
      render: (text) =>
        renderNullable(text, (t) => {
          const short = t.length > 20 ? `${t.slice(0, 20)}...` : t;
          return (
            <Tooltip title={t}>
              <span className="break-by-word">{short}</span>
            </Tooltip>
          );
        }),
    },
    {
      title: "Razón Social",
      dataIndex: "NombreCompleto",
      key: "NombreCompleto",
      width: 200,
      responsive: ["lg"],
      render: (text) =>
        renderNullable(text, (t) => {
          const short = t.length > 20 ? `${t.slice(0, 20)}...` : t;
          return (
            <Tooltip title={t}>
              <span className="break-by-word">{short}</span>
            </Tooltip>
          );
        }),
    },
    {
      title: "Tensión",
      dataIndex: "TensionElectricaSuministro",
      key: "TensionElectricaSuministro",
      align: "right",
      width: 120,
      responsive: ["md"],
      render: (text) => renderNullable(text, (t) => `${t.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} V`),
    },
    {
      title: "Carga",
      dataIndex: "CargaInstalada",
      key: "CargaInstalada",
      align: "right",
      width: 120,
      responsive: ["md"],
      render: (text) => renderNullable(text, (t) => `${Number(t).toFixed(2)} kW`),
    },
    {
      title: "Alcance",
      dataIndex: "AlcanceVerificacion",
      key: "AlcanceVerificacion",
      align: "right",
      width: 120,
      responsive: ["md"],
      render: (text) => renderNullable(text, (t) => `${Number(t).toFixed(2)} kW`),
    },
    {
      title: "Capacidad",
      dataIndex: "Capacidad",
      key: "Capacidad",
      align: "right",
      width: 120,
      responsive: ["md"],
      render: (text) => renderNullable(text, (t) => `${Number(t).toFixed(2)} kVA`),
    },
    {
      title: "Cliente",
      dataIndex: "NombreCliente",
      key: "NombreCliente",
      width: 200,
      render: (text) =>
        renderNullable(text, (t) => {
          const short = t.length > 20 ? `${t.slice(0, 20)}...` : t;
          return (
            <Tooltip title={t}>
              <span className="break-by-word">{short}</span>
            </Tooltip>
          );
        }),
    },
    {
      title: "Monto",
      dataIndex: "Monto",
      key: "Monto",
      align: "right",
      width: 110,
      fixed: "right",
      render: (text) =>
        renderNullable(
          text,
          (t) =>
            `$${Number(t)
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
        ),
    },
  ];

  if (userRole === "administrador" || userRole === "director") {
    columns.push({
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      render: (text, record) => (
        <Tooltip
          title={
            <>
              Ver pagos para la verificación <strong>{record.ControlInterno}</strong>
            </>
          }
        >
          <Link to={`/pagos/${record.VerificacionID}`}>Ver Pagos</Link>
        </Tooltip>
      ),
    });
  }

  return (
    <div>
      <style>
        {`
          .status-border-1 td:first-child,
          .status-border-1.ant-table-row-selected td:first-child {
            border-left: 4px solid #d9d9d9 !important;
            border-radius: 0 0 0 6px;
            position: relative;
          }
          .status-border-1 td:first-child::before,
          .status-border-1.ant-table-row-selected td:first-child::before {
            content: '';
            position: absolute;
            left: -4px;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, rgba(217, 217, 217, 0.3) 0%, #d9d9d9 50%, rgba(217, 217, 217, 0.3) 100%);
            border-radius: 0 2px 2px 0;
          }
          
          .status-border-2 td:first-child,
          .status-border-2.ant-table-row-selected td:first-child {
            border-left: 4px solid #00bfff !important;
            border-radius: 0 0 0 6px;
            position: relative;
          }
          .status-border-2 td:first-child::before,
          .status-border-2.ant-table-row-selected td:first-child::before {
            content: '';
            position: absolute;
            left: -4px;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, rgba(0, 191, 255, 0.3) 0%, #00bfff 50%, rgba(0, 191, 255, 0.3) 100%);
            border-radius: 0 2px 2px 0;
          }
          
          .status-border-3 td:first-child,
          .status-border-3.ant-table-row-selected td:first-child {
            border-left: 4px solid #52c41a !important;
            border-radius: 0 0 0 6px;
            position: relative;
          }
          .status-border-3 td:first-child::before,
          .status-border-3.ant-table-row-selected td:first-child::before {
            content: '';
            position: absolute;
            left: -4px;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, rgba(82, 196, 26, 0.3) 0%, #52c41a 50%, rgba(82, 196, 26, 0.3) 100%);
            border-radius: 0 2px 2px 0;
          }
          
          .status-border-4 td:first-child,
          .status-border-4.ant-table-row-selected td:first-child {
            border-left: 4px solid #52c41a !important;
            border-radius: 0 0 0 6px;
            position: relative;
          }
          .status-border-4 td:first-child::before,
          .status-border-4.ant-table-row-selected td:first-child::before {
            content: '';
            position: absolute;
            left: -4px;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, rgba(82, 196, 26, 0.3) 0%, #52c41a 50%, rgba(82, 196, 26, 0.3) 100%);
            border-radius: 0 2px 2px 0;
          }
          
          .status-border-5 td:first-child,
          .status-border-5.ant-table-row-selected td:first-child {
            border-left: 4px solid red !important;
            border-radius: 0 0 0 6px;
            position: relative;
          }
          .status-border-5 td:first-child::before,
          .status-border-5.ant-table-row-selected td:first-child::before {
            content: '';
            position: absolute;
            left: -4px;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(180deg, rgba(255, 0, 0, 0.3) 0%, red 50%, rgba(255, 0, 0, 0.3) 100%);
            border-radius: 0 2px 2px 0;
          }
        `}
      </style>
      <Table
        rowSelection={
          userRole === "administrador"
            ? null
            : {
                type: "radio",
                onSelect: (record) => {
                  setSelectedVerification(record.VerificacionID);
                },
              }
        }
        columns={columns}
        dataSource={verifications}
        loading={loading}
        rowKey="VerificacionID"
        rowClassName={getRowClassName}
        pagination={{
          pageSize: 15,
          showSizeChanger: true,
          showLessItems: true,
        }}
        scroll={{
          x: "max-content",
        }}
      />
    </div>
  );
}
